import type { InventoryBalance } from "../api/types";
import { knownCostQtyOf, unknownCostQtyOf } from "./inventoryProducts";

const valueEurFmt = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Format a totals-style EUR amount (inventory valuation). */
export function formatInventoryValueEur(n: number): string {
  if (!Number.isFinite(n)) return "—";
  return valueEurFmt.format(n);
}

export type LocationInventoryCategoryStats = {
  categoryCode: string;
  categoryLabel: string;
  distinctItems: number;
  valueEur: number;
  /**
   * WO-B part 6: items with units left out of `valueEur` for want of a known
   * cost — counted as items, because their units (stems, cm, pieces) do not add up.
   */
  unknownCostItemCount: number;
};

export type LocationInventoryStatsResult = {
  totalDistinctItems: number;
  totalValueEur: number;
  /** WO-B part 6: items with units left out of `totalValueEur` (see above). */
  unknownCostItemCount: number;
  byCategory: LocationInventoryCategoryStats[];
};

type ItemAgg = { qtySum: number; valueSum: number; unknownCostQty: number; categoryCode: string };

/**
 * Aggregates full-shop balance rows: distinct catalog items (qty > 0) and extended EUR value where avg unit cost exists.
 */
export function computeLocationInventoryStats(
  rows: InventoryBalance[],
  categoryLabelByCode: Map<string, string>,
): LocationInventoryStatsResult {
  const byItemId = new Map<string, ItemAgg>();

  for (const row of rows) {
    const qty = Number(row.qtyOnHand);
    if (!Number.isFinite(qty) || qty <= 0) continue;

    const code = row.item.category.code;
    const costStr = row.avgPurchaseUnitCostEur;
    // WO-B part 4: the average prices only the units of known cost.
    const lineValue =
      costStr != null && costStr !== ""
        ? knownCostQtyOf(row) * Number(costStr)
        : 0;
    const safeLine = Number.isFinite(lineValue) ? lineValue : 0;
    // WO-B part 6: the units that value leaves out (none where no price applies).
    const unknownQty = unknownCostQtyOf(row) ?? 0;

    const prev = byItemId.get(row.item.id);
    if (prev) {
      prev.qtySum += qty;
      prev.valueSum += safeLine;
      prev.unknownCostQty += unknownQty;
    } else {
      byItemId.set(row.item.id, {
        qtySum: qty,
        valueSum: safeLine,
        unknownCostQty: unknownQty,
        categoryCode: code,
      });
    }
  }

  const totalDistinctItems = byItemId.size;
  let totalValueEur = 0;
  let unknownCostItemCount = 0;
  for (const agg of byItemId.values()) {
    totalValueEur += agg.valueSum;
    if (agg.unknownCostQty > 0) unknownCostItemCount += 1;
  }

  const categoryDistinct = new Map<string, Set<string>>();
  const categoryValue = new Map<string, number>();
  const categoryUnknown = new Map<string, number>();

  for (const [itemId, agg] of byItemId) {
    const code = agg.categoryCode;
    if (!categoryDistinct.has(code)) {
      categoryDistinct.set(code, new Set());
      categoryValue.set(code, 0);
      categoryUnknown.set(code, 0);
    }
    categoryDistinct.get(code)!.add(itemId);
    categoryValue.set(code, (categoryValue.get(code) ?? 0) + agg.valueSum);
    if (agg.unknownCostQty > 0) categoryUnknown.set(code, (categoryUnknown.get(code) ?? 0) + 1);
  }

  const byCategory: LocationInventoryCategoryStats[] = [];
  for (const code of categoryDistinct.keys()) {
    const distinctItems = categoryDistinct.get(code)!.size;
    const valueEur = categoryValue.get(code) ?? 0;
    const unknownCostItemCount = categoryUnknown.get(code) ?? 0;
    const categoryLabel = categoryLabelByCode.get(code) ?? code;
    byCategory.push({ categoryCode: code, categoryLabel, distinctItems, valueEur, unknownCostItemCount });
  }

  byCategory.sort((a, b) =>
    a.categoryLabel.localeCompare(b.categoryLabel, undefined, { sensitivity: "base" }),
  );

  return { totalDistinctItems, totalValueEur, unknownCostItemCount, byCategory };
}
